import { Apis } from "../../../lib/Apis";

/** Maps response group keys (camelCase) to API targetType (snake_case), e.g. houseDesign → house_design. */
const apiGroupKeyToTargetType = (key) => {
    if (key == null || typeof key !== "string") return key;
    return key.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
};

/**
 * Similar-items GET may return:
 * - Legacy: an array of link rows, or { items: [...] }
 * - Current: grouped entities, e.g. { blog: [...], houseDesign: [...], interiorDesign: [...] }
 */
const normalizeSimilarItemsList = (payload) => {
    if (payload == null) return [];
    if (Array.isArray(payload)) return payload;
    if (typeof payload !== "object") return [];
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.data)) return payload.data;

    const rows = [];
    for (const [groupKey, list] of Object.entries(payload)) {
        if (!Array.isArray(list)) continue;
        const targetType = apiGroupKeyToTargetType(groupKey);
        for (const item of list) {
            if (!item || typeof item !== "object") continue;
            const targetId = item.id;
            const linkId =
                item.similarItemId ??
                item.similar_item_id ??
                item.linkId ??
                item.link_id ??
                targetId;
            const similarItemPosition =
                item.similarItemPosition ??
                item.similar_item_position ??
                null;
            rows.push({
                id: linkId,
                targetId,
                targetType,
                position: item.position,
                similarItemPosition,
                targetItem: item,
            });
        }
    }
    const sortKey = (row) =>
        row.similarItemPosition ?? row.position ?? 0;
    return rows.sort((a, b) => sortKey(a) - sortKey(b));
};

export const useSimilarItems = (sourceType) => {
    const getSimilarItemsData = async (setSimilarItemsData, sourceId) => {
        try {
            const response = await Apis.getSimilarItems({
                sourceId,
                sourceType
            });
            const list = normalizeSimilarItemsList(response.data?.data);
            setSimilarItemsData(list);
            return list;
        } catch (error) {
            console.error(error);
            return error;
        }
    };

    const getItemsListingForSelection = async (setListingData, sourceId, targetType = sourceType, search = "") => {
        try {
            const response = await Apis.getAdminSimilarItemsList({
                sourceId,
                sourceType,
                targetType,
                search,
                limit: 100 
            });
            setListingData(response.data.data.items);
            return response.data.data.items;
        } catch (error) {
            console.error(error);
            return error;
        }
    };

    const addSimilarItems = async (sourceId, targetItems) => {
        try {
            const response = await Apis.addSimilarItems({
                sourceId,
                sourceType,
                targetItems
            });
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const deleteSimilarItem = async (sourceId, targetId, targetType) => {
        try {
            const response = await Apis.removeSimilarItem({
                sourceId,
                sourceType,
                targetId,
                targetType
            });
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const updateSimilarItemPosition = async (id, similarItemPosition) => {
        try {
            const response = await Apis.updateSimilarItemPosition({
                id,
                similarItemPosition,
            });
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    return {
        getSimilarItemsData,
        addSimilarItems,
        deleteSimilarItem,
        getItemsListingForSelection,
        updateSimilarItemPosition,
    };
};
